import { useState } from "react";
import { Loader2, Sparkles, Volume2, Mic } from "lucide-react";
import { DiffView, DiffLegend } from "@/components/DiffView";
import { api, errMsg } from "@/lib/api";
import { speak, cancelSpeech, unlockAudio } from "@/lib/speech";
import { PronunciationPanel } from "@/components/PronunciationPanel";
import { SaveMissedWords } from "@/components/SaveWords";
import { SpeakingTips } from "@/components/SpeakingTips";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const modelText = (k, item) => ({ A: item.text, B: item.text, G: item.text, C: item.answer, D: item.answers?.[0] ? `${item.question} ${item.answers[0]}.` : null }[k]);

const ListenBack = ({ partKey, item, response, rate }) => {
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const text = modelText(partKey, item);
  if (!text) return null;
  const playModel = async () => { unlockAudio(true); cancelSpeech(); setPlaying(true); setFailed(false); const r = await speak(text, { rate }); setPlaying(false); setFailed(!r.ok); };
  const spoken = ["A", "B", "C", "D"].includes(partKey);
  return (
    <div data-testid="listen-back-panel" className="grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2">
      {spoken && (response.audioUrl || response.noMic) && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500"><Mic className="h-3 w-3" />Your recording</div>
          {response.audioUrl
            ? <audio data-testid="my-recording-audio" controls playsInline preload="metadata" src={response.audioUrl} className="h-10 w-full" />
            : <p data-testid="no-recording-msg" className="text-xs text-slate-400">No recording captured — the microphone was blocked or unavailable.</p>}
        </div>
      )}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500"><Volume2 className="h-3 w-3" />Model answer</div>
        <button data-testid="play-model-audio-btn" onClick={playModel} disabled={playing} className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-700 disabled:opacity-60">
          <Volume2 className="h-3.5 w-3.5" />{playing ? "Playing…" : "Play model audio"}</button>
        {failed && <p data-testid="model-audio-error" className="text-xs text-rose-600">Sound was blocked. Turn off silent mode and tap again.</p>}
      </div>
    </div>
  );
};

const Pct = ({ v }) => {
  const p = Math.round(v * 100);
  return <span data-testid="feedback-score" className={`font-mono text-sm font-bold ${p >= 70 ? "text-emerald-600" : p >= 40 ? "text-amber-600" : "text-rose-600"}`}>{p}%</span>;
};

// AI coaching is part of the Practice Pass; free users see a short upsell instead
const AiOnly = ({ children }) => {
  const { hasAccess } = useAuth();
  if (hasAccess) return children;
  return <p data-testid="ai-upsell" className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-900">AI coaching and written feedback are included with a <Link to="/pricing" className="font-semibold underline">Practice Pass</Link>.</p>;
};

const AiGrade = ({ partKey, item, response }) => {
  const [state, setState] = useState({ loading: false });
  const run = async () => {
    setState({ loading: true });
    try {
      const { data } = await api.post("/ai/grade", { part: partKey, passage: item.text, response: response.text || "", summary: response.summary || "", opinion: response.opinion || "" });
      setState({ data });
    } catch (e) { setState({ error: errMsg(e) }); }
  };
  if (state.data) return (
    <div data-testid="ai-grade-result" className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-sm text-slate-700">
      <div className="mb-2 flex flex-wrap gap-4 font-mono text-xs text-indigo-700">
        <span>AI SCORE {state.data.score}/100</span><span>CONTENT {state.data.content}</span><span>GRAMMAR {state.data.grammar}</span><span>VOCAB {state.data.vocabulary}</span>
      </div>
      <p>{state.data.feedback}</p>
    </div>
  );
  return (
    <div>
      <button data-testid="ai-grade-btn" onClick={run} disabled={state.loading} className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-60">
        {state.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Get AI rater feedback
      </button>
      {state.error && <p data-testid="ai-grade-error" className="mt-2 text-sm text-rose-600">{state.error}</p>}
    </div>
  );
};

const Row = ({ label, children }) => (
  <div className="min-w-0 space-y-1"><div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400">{label}</div>{children}</div>
);

export const Feedback = ({ partKey, item, response = {}, result, rate = 0.95, autoAi = false }) => (
  <div data-testid="feedback-panel" className="wrap-anywhere w-full min-w-0 max-w-full space-y-4">
    <div className="flex items-center justify-between"><span className="text-sm font-semibold text-slate-700">Item score</span><Pct v={result.score} /></div>
    <ListenBack partKey={partKey} item={item} response={response} rate={rate} />
    {partKey === "D" && <Row label="Conversation">{item.turns.map((t, i) => <p key={i} className="text-sm text-slate-700"><b>{t.speaker}:</b> {t.text}</p>)}<p className="text-sm italic text-slate-600">Q: {item.question}</p></Row>}
    {partKey === "C" && <Row label="Phrases heard"><p className="text-sm text-slate-600">{item.chunks.join("  /  ")}</p></Row>}
    {partKey === "F" && <Row label="Sentence"><p className="text-sm text-slate-700">{item.sentence}</p></Row>}
    {partKey === "I" && <Row label="Summary & opinion">
      <p className="text-sm text-slate-700"><b>Summary ({result.summaryWords} words):</b> {response.summary || "—"}</p>
      <p className="text-sm text-slate-700"><b>Opinion ({result.opinionWords} words):</b> {response.opinion || "—"}</p>
    </Row>}
    {!["I", "E"].includes(partKey) && <Row label="Your response"><p data-testid="feedback-response" className="text-sm text-slate-800">{response.text || <span className="text-slate-400">No response</span>}</p></Row>}
    {result.accepted && <Row label={result.correct ? "Correct — accepted answers" : "Accepted answers"}>
      <div className="flex flex-wrap gap-1">{result.accepted.map((a) => <span key={a} className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">{a}</span>)}</div>
    </Row>}
    {partKey === "E" && <div className="grid grid-cols-3 gap-3 font-mono text-sm">
      <div className="rounded-lg bg-slate-100 p-3"><div className="text-xs text-slate-500">WPM</div><b data-testid="feedback-wpm">{result.wpm}</b></div>
      <div className="rounded-lg bg-slate-100 p-3"><div className="text-xs text-slate-500">NET WPM</div><b>{result.netWpm}</b></div>
      <div className="rounded-lg bg-slate-100 p-3"><div className="text-xs text-slate-500">ACCURACY</div><b data-testid="feedback-accuracy">{Math.round(result.accuracy * 100)}%</b></div>
    </div>}
    {result.diff && ["A", "B"].includes(partKey) && <PronunciationPanel diff={result.diff} response={response} />}
    {result.diff && <Row label="Word diff vs. target"><DiffView diff={result.diff} /><DiffLegend /></Row>}
    {result.ref && partKey !== "E" && <Row label="Target"><p className="text-sm text-slate-600">{result.ref}</p></Row>}
    {result.hits && <Row label="Key ideas">
      <div className="flex flex-wrap gap-1">
        {result.hits.map((k) => <span key={k} className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800">{k}</span>)}
        {result.missed.map((k) => <span key={k} className="rounded-full bg-rose-100 px-2 py-0.5 text-xs text-rose-700 line-through">{k}</span>)}
      </div>
    </Row>}
    {["H", "I"].includes(partKey) && <AiOnly><AiGrade partKey={partKey} item={item} response={response} /></AiOnly>}
    {["A", "B", "C", "D"].includes(partKey) && <AiOnly><SpeakingTips partKey={partKey} item={item} response={response} auto={autoAi} /></AiOnly>}
    <SaveMissedWords partKey={partKey} item={item} result={result} />
  </div>
);
